import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n';
import styles from './LegalPage.module.css';

interface LegalPageProps {
  type: 'privacy' | 'terms' | 'cookies' | 'return' | 'dataDeletion';
}

export function LegalPage({ type }: LegalPageProps) {
  const { t, language } = useLanguage();

  const getContent = () => {
    switch (type) {
      case 'privacy':
        return t.policies.privacy;
      case 'terms':
        return t.policies.terms;
      case 'cookies':
        return t.policies.cookies;
      case 'return':
        return getReturnPolicy();
      case 'dataDeletion':
        return getDataDeletionPolicy();
      default:
        return '';
    }
  };

  const getReturnPolicy = () => {
    if (language === 'tr') {
      return `<h2>İptal ve İade Politikası</h2>

<h3>Dijital Hizmet Niteliği</h3>
<p>Polaris, dijital bir yazılım hizmeti (SaaS - Software as a Service) olup, fiziksel bir ürün değildir. Ödeme onaylandıktan sonra hizmet anında aktif hale gelir ve kullanıcı hesabı üzerinden erişilebilir.</p>

<h3>Cayma Hakkı</h3>
<p>6502 sayılı Tüketicinin Korunması Hakkında Kanun'un 48. maddesi ve Mesafeli Sözleşmeler Yönetmeliği'nin 15. maddesi uyarınca, elektronik ortamda anında ifa edilen hizmetler ve tüketiciye anında teslim edilen gayri maddi mallarda cayma hakkı kullanılamaz.</p>

<p>Polaris hizmeti, ödeme işleminin tamamlanmasının ardından anında aktif hale geldiğinden, bu kapsamda değerlendirilmektedir. Bu nedenle, hizmetin aktif edilmesinden sonra cayma hakkı bulunmamaktadır.</p>

<h3>Abonelik İptali</h3>
<p>Kullanıcılar, abonelik dönemleri sona ermeden önce herhangi bir zamanda yenilemeyi iptal edebilirler. İptal durumunda:</p>
<ul>
<li>Mevcut abonelik dönemi sonuna kadar hizmet kullanımına devam edilebilir.</li>
<li>Kullanılmayan süre için kısmi iade yapılabilir. İade tutarı, kalan süre oranında hesaplanır.</li>
<li>Yeni dönem başlamadan önce iptal edilirse, yeni dönem için ücret alınmaz.</li>
</ul>

<h3>İstisnai Durumlar</h3>
<p>Aşağıdaki durumlarda iade talepleri değerlendirmeye alınabilir:</p>
<ul>
<li>Teknik nedenlerle hizmetin hiç sağlanamaması</li>
<li>Ödeme hatası veya mükerrer ödeme</li>
<li>Yasal zorunluluklar</li>
</ul>

<h3>İade Süreci</h3>
<p>İade talebinde bulunmak için info@polysansoft.com adresine e-posta gönderiniz. Talebiniz en geç 10 iş günü içinde değerlendirilecektir. Onaylanan iadeler, ödeme yönteminize bağlı olarak 2-4 hafta içinde hesabınıza yansıyacaktır.</p>

<h3>İletişim</h3>
<p>İptal ve iade konularında sorularınız için:</p>
<ul>
<li>E-posta: info@polysansoft.com</li>
<li>Telefon: +90 532 169 0938</li>
<li>Adres: Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri</li>
</ul>`;
    }

    return `<h2>Cancellation and Refund Policy</h2>

<h3>Digital Service Nature</h3>
<p>Polaris is a digital software service (SaaS - Software as a Service), not a physical product. The service is activated immediately upon payment confirmation and is accessible through the user's account on the platform.</p>

<h3>Right of Withdrawal</h3>
<p>In accordance with Turkish Consumer Protection Law No. 6502 Article 48 and the Distance Contracts Regulation Article 15, the right of withdrawal cannot be exercised for services that are immediately performed electronically and intangible goods that are immediately delivered to the consumer.</p>

<p>As Polaris service is activated immediately after payment completion, it falls under this scope. Therefore, there is no right of withdrawal after the service has been activated.</p>

<h3>Subscription Cancellation</h3>
<p>Users can cancel renewal at any time before their subscription period ends. In case of cancellation:</p>
<ul>
<li>Service usage can continue until the end of the current subscription period.</li>
<li>Partial refund is available for unused time. Refund amount is calculated proportionally to the remaining period.</li>
<li>If cancelled before the new period starts, no charge will be made for the new period.</li>
</ul>

<h3>Exceptional Circumstances</h3>
<p>Refund requests may be considered in the following situations:</p>
<ul>
<li>Service could not be provided at all due to technical reasons</li>
<li>Payment error or duplicate payment</li>
<li>Legal requirements</li>
</ul>

<h3>Refund Process</h3>
<p>To request a refund, please send an email to info@polysansoft.com. Your request will be evaluated within 10 business days at the latest. Approved refunds will be reflected in your account within 2-4 weeks depending on your payment method.</p>

<h3>Contact</h3>
<p>For questions about cancellation and refunds:</p>
<ul>
<li>Email: info@polysansoft.com</li>
<li>Phone: +90 532 169 0938</li>
<li>Address: Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri</li>
</ul>`;
  };

  const getDataDeletionPolicy = () => {
    if (language === 'tr') {
      return `<h2>Veri Silme Talebi</h2>

<h3>KVKK Kapsamında Haklarınız</h3>
<p>6698 sayılı Kişisel Verilerin Korunması Kanunu (KVKK) 11. maddesi uyarınca, kişisel verilerinizin silinmesini, yok edilmesini veya anonim hale getirilmesini talep etme hakkınız bulunmaktadır. Polaris olarak veri sahibi olarak haklarınıza saygı duyuyor ve bu talebinizi en kısa sürede yerine getiriyoruz.</p>

<h3>Verilerinizin Silinmesi İçin</h3>
<p>Hesabınıza ve kişisel verilerinize ilişkin silme talebinizi iletmek için, kayıtlı e-posta adresinizden aşağıdaki adrese e-posta göndermeniz yeterlidir:</p>

<p style="font-size: 1.1em; margin: 1em 0;"><strong><a href="mailto:info@polysansoft.com?subject=Veri%20Silme%20Talebi">info@polysansoft.com</a></strong></p>

<p>E-postanızda lütfen aşağıdaki bilgileri belirtiniz:</p>
<ul>
<li>Ad - Soyad</li>
<li>Hesabınızla ilişkili e-posta adresi</li>
<li>Talebinizin konusu (verilerin tamamen silinmesi, belirli verilerin silinmesi vb.)</li>
<li>Kimliğinizi doğrulayabileceğimiz bilgiler</li>
</ul>

<h3>Sürecin İşleyişi</h3>
<p>KVKK'nın 13. maddesi gereğince, talebiniz tarafımıza ulaştıktan sonra en geç <strong>30 (otuz) gün</strong> içerisinde sonuçlandırılacaktır. Kimlik doğrulama işleminin ardından:</p>
<ul>
<li>Hesabınız ve tüm kişisel verileriniz sistemlerimizden silinecektir.</li>
<li>Yedekleme sistemlerimizdeki kayıtlar, periyodik yedek silme süreci içinde geri dönüşü olmayacak şekilde imha edilecektir.</li>
<li>İşlemin tamamlandığına dair e-posta yoluyla bilgilendirme yapılacaktır.</li>
</ul>

<h3>Yasal Saklama Yükümlülükleri</h3>
<p>6502 sayılı Tüketicinin Korunması Hakkında Kanun, 213 sayılı Vergi Usul Kanunu ve 6098 sayılı Türk Borçlar Kanunu kapsamındaki yasal saklama yükümlülüklerimiz nedeniyle, fatura, ödeme ve sözleşme bilgileri gibi bazı veriler yasal saklama süresi boyunca (genellikle 10 yıl) sistemlerimizde tutulmaya devam eder. Bu süre sonunda söz konusu veriler de imha edilir.</p>

<h3>Veri Sahibinin Diğer Hakları</h3>
<p>KVKK kapsamında, silme talebi dışında aşağıdaki haklarınızı da kullanabilirsiniz:</p>
<ul>
<li>Kişisel verilerinizin işlenip işlenmediğini öğrenme</li>
<li>İşlenmişse buna ilişkin bilgi talep etme</li>
<li>İşlenme amacını ve amaca uygun kullanılıp kullanılmadığını öğrenme</li>
<li>Yurt içi/yurt dışında aktarıldığı üçüncü kişileri öğrenme</li>
<li>Eksik veya yanlış işlenmiş verilerin düzeltilmesini isteme</li>
<li>İşlemeye itiraz etme</li>
<li>Kanuna aykırı işleme nedeniyle zarara uğranılması halinde tazminat talep etme</li>
</ul>

<h3>İletişim</h3>
<p>Veri sahibi haklarınızla ilgili tüm talep ve sorularınız için:</p>
<ul>
<li>E-posta: <a href="mailto:info@polysansoft.com">info@polysansoft.com</a></li>
<li>Telefon: +90 352 606 0024</li>
<li>Adres: Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri</li>
</ul>`;
    }

    return `<h2>Data Deletion Request</h2>

<h3>Your Rights Under KVKK / GDPR</h3>
<p>Under Article 11 of the Turkish Personal Data Protection Law No. 6698 (KVKK) — and, where applicable, the EU General Data Protection Regulation (GDPR) — you have the right to request the deletion, destruction, or anonymization of your personal data. As Polaris, we respect your rights as a data subject and process such requests promptly.</p>

<h3>How to Request Deletion</h3>
<p>To submit a deletion request for your account and personal data, simply send an email from your registered email address to:</p>

<p style="font-size: 1.1em; margin: 1em 0;"><strong><a href="mailto:info@polysansoft.com?subject=Data%20Deletion%20Request">info@polysansoft.com</a></strong></p>

<p>Please include the following information in your email:</p>
<ul>
<li>Full name</li>
<li>Email address associated with your account</li>
<li>Scope of your request (full deletion of all data, deletion of specific data, etc.)</li>
<li>Information allowing us to verify your identity</li>
</ul>

<h3>Processing Timeline</h3>
<p>In accordance with Article 13 of KVKK, your request will be processed within <strong>30 (thirty) days</strong> at the latest. Following identity verification:</p>
<ul>
<li>Your account and all personal data will be deleted from our active systems.</li>
<li>Records held in backup systems will be irreversibly destroyed during the periodic backup purge cycle.</li>
<li>You will receive an email confirmation once the process is completed.</li>
</ul>

<h3>Legal Retention Obligations</h3>
<p>Due to our legal retention obligations under the Turkish Consumer Protection Law No. 6502, Tax Procedure Law No. 213, and Turkish Code of Obligations No. 6098, certain data such as invoices, payment records, and contractual information must remain in our systems for the legally mandated retention period (typically 10 years). Such data is destroyed at the end of that period.</p>

<h3>Other Data Subject Rights</h3>
<p>Beyond the right to deletion, under KVKK you also have the right to:</p>
<ul>
<li>Learn whether your personal data is being processed</li>
<li>Request information regarding such processing</li>
<li>Learn the purpose of processing and whether the data is used in accordance with that purpose</li>
<li>Learn the third parties to whom data is transferred domestically or abroad</li>
<li>Request correction of incomplete or inaccurate data</li>
<li>Object to the processing</li>
<li>Claim compensation in case of damages arising from unlawful processing</li>
</ul>

<h3>Contact</h3>
<p>For all requests and questions regarding your data subject rights:</p>
<ul>
<li>Email: <a href="mailto:info@polysansoft.com">info@polysansoft.com</a></li>
<li>Phone: +90 352 606 0024</li>
<li>Address: Köşk mah. Köşk cad. 55/25 Melikgazi / Kayseri</li>
</ul>`;
  };

  const getTitle = () => {
    switch (type) {
      case 'privacy':
        return t.footer.privacyPolicy;
      case 'terms':
        return t.footer.termsOfService;
      case 'cookies':
        return t.footer.cookiePolicy;
      case 'return':
        return t.footer.returnPolicy;
      case 'dataDeletion':
        return t.footer.dataDeletion;
      default:
        return '';
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Link to="/" className={styles.logo}>
          <img src="/polaris.svg" alt="Polaris" width="32" height="32" />
          <span>Polaris</span>
        </Link>
      </div>

      <div className={styles.content}>
        <nav className={styles.breadcrumb}>
          <Link to="/">{language === 'tr' ? 'Ana Sayfa' : 'Home'}</Link>
          <span>/</span>
          <span>{getTitle()}</span>
        </nav>

        <article
          className={styles.policy}
          dangerouslySetInnerHTML={{ __html: getContent() }}
        />

        <div className={styles.otherLinks}>
          <h4>{language === 'tr' ? 'Diğer Yasal Belgeler' : 'Other Legal Documents'}</h4>
          <div className={styles.linkGrid}>
            {type !== 'privacy' && (
              <Link to="/privacy-policy">{t.footer.privacyPolicy}</Link>
            )}
            {type !== 'terms' && (
              <Link to="/terms-of-service">{t.footer.termsOfService}</Link>
            )}
            {type !== 'return' && (
              <Link to="/return-policy">{t.footer.returnPolicy}</Link>
            )}
            {type !== 'cookies' && (
              <Link to="/cookie-policy">{t.footer.cookiePolicy}</Link>
            )}
            {type !== 'dataDeletion' && (
              <Link to="/data-deletion">{t.footer.dataDeletion}</Link>
            )}
          </div>
        </div>

        <div className={styles.companyInfo}>
          <p><strong>{t.footer.companyName}</strong> <span className={styles.companyType}>({t.footer.companyType})</span></p>
          <p>{t.footer.address}</p>
          <p>
            <a href={`tel:${t.footer.phone.replace(/\s/g, '')}`}>{t.footer.phone}</a>
            {' | '}
            <a href={`mailto:${t.footer.email}`}>{t.footer.email}</a>
          </p>
          <p className={styles.copyright}>© {new Date().getFullYear()} Polaris - {t.footer.aPolysanProduct}</p>
        </div>
      </div>
    </div>
  );
}
